// DTOs for /api/trainer. These mirror the Java records in
// backend/trainer/src/main/java/pl/thatisit/trainer/dto. Field names are the JSON names.
// Java boxed numbers (Double, Integer, Long) can be null, so they are typed `| null`.
// Dates: LocalDate is "YYYY-MM-DD". Instant and OffsetDateTime are ISO 8601 strings.

/** GET /me */
export interface TrainerMe {
  id: number;
  email: string;
  name: string | null;
}

/** Paging shape used by every list endpoint (PageDto). */
export interface Page<T> {
  items: T[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

/** Garmin activity type key as stored from Garmin (activityType.typeKey), e.g. "running". Not a fixed enum. */
export type ActivityType = string;

/** GET /activities, GET /activities/{id} (ActivityDto). rawJson is only sent with includeRaw=true, which the UI never asks for. */
export interface Activity {
  id: number;
  garminId: number | null;
  startTime: string;
  activityType: ActivityType | null;
  name: string | null;
  distanceM: number | null;
  durationS: number | null;
  movingS: number | null;
  avgHr: number | null;
  maxHr: number | null;
  avgPaceSPerKm: number | null;
  elevationGainM: number | null;
  calories: number | null;
  avgCadence: number | null;
  aerobicTrainingEffect: number | null;
  fitDownloaded: boolean;
}

/** GET /activities/{id}/laps (LapDto). */
export interface ActivityLap {
  index: number;
  startOffsetS: number | null;
  durationS: number | null;
  distanceM: number | null;
  avgHr: number | null;
  maxHr: number | null;
  avgPaceSPerKm: number | null;
  avgCadence: number | null;
  ascentM: number | null;
}

/** GET /activities/{id}/samples (SampleDto). One point per recorded second or so. */
export interface ActivitySample {
  timeS: number;
  distanceM: number | null;
  hr: number | null;
  paceSPerKm: number | null;
  altitudeM: number | null;
  cadence: number | null;
  lat: number | null;
  lon: number | null;
}

/** PUT /activities/{id}/adjustment response (AdjustmentDto). */
export interface ActivityAdjustment {
  activityId: number | null;
  officialDistanceM: number;
  officialTimeS: number;
  note: string | null;
  updatedAt: string;
}

/** PUT /activities/{id}/adjustment body (AdjustmentRequest). */
export interface ActivityAdjustmentRequest {
  officialDistanceM: number;
  officialTimeS: number;
  note?: string | null;
}

/** GET /health/daily (DailyDto). One row per day. */
export interface DailyHealth {
  date: string;
  steps: number | null;
  calories: number | null;
  restingHr: number | null;
  stressAvg: number | null;
  intensityMinutes: number | null;
}

/** GET /health/sleep (SleepDto). Durations are seconds. */
export interface SleepNight {
  date: string;
  totalSleepS: number | null;
  deepSleepS: number | null;
  lightSleepS: number | null;
  remSleepS: number | null;
  awakeS: number | null;
  sleepScore: number | null;
}

/** GET /health/hrv (HrvDto). */
export interface HrvDay {
  date: string;
  weeklyAvg: number | null;
  lastNightAvg: number | null;
  lastNight5MinHigh: number | null;
  status: string | null;
}

/** GET /health/body-battery (BodyBatteryDto). */
export interface BodyBatteryDay {
  date: string;
  highest: number | null;
  lowest: number | null;
  charged: number | null;
  drained: number | null;
}

export type HealthResource = 'daily' | 'sleep' | 'hrv' | 'body-battery';

/** Catalog distance codes (DistanceCatalogSeeder). The codes are part of the API. */
export type RecordDistance =
  | '400m'
  | '800m'
  | '1000m'
  | '1mile'
  | '3k'
  | '5k'
  | '10k'
  | '15k'
  | '10mile'
  | 'half'
  | 'marathon';

export type RecordSource = 'auto' | 'adjusted' | 'manual';

/** RecordValue. source says where the time came from; activityId and date say which run. note is set for manual values. */
export interface RecordValue {
  timeS: number | null;
  source: RecordSource;
  activityId: number | null;
  date: string | null;
  note: string | null;
}

/** One catalog distance. effective is what is in use (the override when there is one). */
export interface RecordItem {
  distance: RecordDistance;
  name: string;
  meters: number;
  effective: RecordValue | null;
  automatic: RecordValue | null;
  override: RecordValue | null;
}

/** GET /records */
export interface RecordsResponse {
  items: RecordItem[];
}

/** PUT /records/{distance} body (OverrideRequest). timeS is required. */
export interface RecordOverrideRequest {
  timeS: number;
  date?: string | null;
  activityId?: number | null;
  note?: string | null;
}

/** One effort on the progression graph (ProgressionEffort). paceSPerKm is timeS over the catalog distance. */
export interface ProgressionEffort {
  timeS: number;
  paceSPerKm: number;
  activityId: number | null;
  activityName: string | null;
  date: string;
  source: RecordSource;
}

/** The best efforts of one calendar year, fastest first. efforts[0] is the yearly best. */
export interface ProgressionYear {
  year: number;
  efforts: ProgressionEffort[];
}

/** One catalog distance on the progression graph. years only lists years with data. */
export interface ProgressionDistance {
  distance: RecordDistance;
  name: string;
  meters: number;
  years: ProgressionYear[];
  allTimeProgression: ProgressionEffort[];
}

/** GET /records/progression */
export interface ProgressionResponse {
  top: number;
  distances: ProgressionDistance[];
}

/** POST /records/recompute */
export interface RecomputeResponse {
  activities: number;
}

/** Manual override shown on the efforts page (GET /records/{distance}/efforts, field override). */
export interface RecordEffortOverride {
  timeS: number;
  source: 'manual';
  activityId: number | null;
  date: string | null;
  note: string | null;
}

/** One run that counts for a distance. source is adjusted when an official result replaced the Garmin split. */
export interface RecordEffort {
  activityId: number;
  activityName: string | null;
  date: string;
  startTime: string;
  timeS: number;
  source: 'auto' | 'adjusted';
  activityDistanceM: number;
  paceSPerKm: number;
}

/** GET /records/{distance}/efforts. Running activities only, fastest first. */
export interface RecordEffortsResponse {
  distance: RecordDistance;
  name: string;
  meters: number;
  limit: number;
  override: RecordEffortOverride | null;
  items: RecordEffort[];
}

/** Row counts offered by the efforts page. */
export const EFFORT_LIMITS = [10, 25, 50, 100] as const;
export type EffortLimit = (typeof EFFORT_LIMITS)[number];

/** Garmin connection state. status is "disconnected" when there is no key, otherwise ok, invalid, expired or revoked. */
export type GarminConnectionStatus = 'disconnected' | 'ok' | 'invalid' | 'expired' | 'revoked';

/** GET /garmin/status (GarminStatusDto). Never contains the key or the token. */
export interface GarminStatus {
  connected: boolean;
  status: GarminConnectionStatus;
  keyExpiresAt: string | null;
  lastCheckedAt: string | null;
  daysUntilExpiry: number | null;
}

/** PUT /garmin/key body (GarminKeyRequest). expiresInDays is 1..365, or omitted for no expiry. */
export interface GarminKeyRequest {
  apiKey: string;
  expiresInDays?: number | null;
}

/** The data types the sync engine keeps in step (SyncDataType). */
export type SyncDataType = 'ACTIVITIES' | 'DAILY' | 'SLEEP' | 'HRV' | 'BODY_BATTERY';

/** Backfill progress. done when the cursor has passed the depth limit. cursor is the newest date not yet backfilled. */
export interface SyncBackfill {
  done: boolean;
  cursor: string | null;
  floor: string;
}

/** One data type in GET /sync/status (SyncStatusDto.TypeStatus). lastStatus is the enum name: OK or ERROR. */
export interface SyncTypeStatus {
  type: SyncDataType;
  lastRunAt: string | null;
  lastStatus: 'OK' | 'ERROR' | null;
  lastError: string | null;
  watermark: string | null;
  backfill: SyncBackfill;
}

/** GET /sync/status and the 202 body of POST /sync */
export interface SyncStatus {
  types: SyncTypeStatus[];
}

/** Error body the backend sends for handled errors: { "error": "message" }. */
export interface ApiErrorBody {
  error?: string;
}

/** GET /health/* range params. Dates are YYYY-MM-DD. */
export interface HealthRange {
  from: string;
  to: string;
}

/** GET /activities params. */
export interface ActivityQuery {
  from?: string;
  to?: string;
  type?: ActivityType;
  page?: number;
  size?: number;
}

export type SummaryGranularity = 'week' | 'month' | 'year';

/** One period of GET /activities/summary. Nulls mean there is nothing to average or sum. */
export interface ActivitySummaryBucket {
  bucketStart: string;
  runs: number;
  distanceM: number;
  durationS: number;
  elevationGainM: number | null;
  avgPaceSPerKm: number | null;
}

/** GET /activities/summary. Empty periods are included. Weeks start on Monday. */
export interface ActivitySummaryResponse {
  granularity: SummaryGranularity;
  from: string;
  to: string;
  zone: string;
  types: ActivityType[];
  buckets: ActivitySummaryBucket[];
}

/** GET /activities/summary params. types is a comma list and is optional (default: the running family). */
export interface ActivitySummaryQuery {
  granularity: SummaryGranularity;
  from: string;
  to: string;
  types?: string;
}
